import { useState } from 'react';
import type { Tenant } from '../types';
import RechargeModal from './RechargeModal';

interface TenantDetailModalProps {
  tenant: Tenant | null;
  onClose: () => void;
  onStatusChange: (id: string, status: 'active' | 'disabled') => void;
}

export default function TenantDetailModal({ tenant, onClose, onStatusChange }: TenantDetailModalProps) {
  const [showRecharge, setShowRecharge] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!tenant) return null;

  const handleStatusToggle = () => {
    if (tenant.status === 'active') {
      setShowConfirm(true);
    } else {
      onStatusChange(tenant.id, 'active');
    }
  };

  const handleConfirmDisable = () => {
    onStatusChange(tenant.id, 'disabled');
    setShowConfirm(false);
  };

  const getSchoolTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      primary: '小学',
      middle: '初中',
      vocational: '职高',
      high: '高中',
      college: '大专',
      university: '大学'
    };
    return labels[type] || type;
  };

  const getNetworkStatusLabel = (status: string) => {
    return status === 'normal' ? '正常' : '维护中';
  };

  const getLatencyColor = (latency: number) => {
    if (latency < 10) return 'text-green-600';
    if (latency < 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPacketLossColor = (loss: number) => {
    if (loss < 0.1) return 'text-green-600';
    if (loss < 0.2) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getUptimeColor = (uptime: number) => {
    if (uptime >= 99.5) return 'text-green-600';
    if (uptime >= 98) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">租户详情</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">租户 ID</label>
                <p className="text-gray-900">{tenant.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">名称</label>
                <p className="text-gray-900">{tenant.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户数量</label>
                <p className="text-gray-900">{tenant.userCount}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">租户状态</label>
                <button
                  onClick={handleStatusToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                    tenant.status === 'active' ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      tenant.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {tenant.type === 'enterprise' && tenant.enterpriseInfo && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">企业信息</h3>
                <div className="grid grid-cols-2 gap-4">
                  {tenant.enterpriseInfo.license && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">企业执照</label>
                      <a
                        href={tenant.enterpriseInfo.license.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-700 flex items-center gap-2"
                      >
                        <i className="ri-file-text-line"></i>
                        {tenant.enterpriseInfo.license.fileName}
                      </a>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">企业名称</label>
                    <p className="text-gray-900">{tenant.enterpriseInfo.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">统一社会信用代码</label>
                    <p className="text-gray-900">{tenant.enterpriseInfo.creditCode}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">法人姓名</label>
                    <p className="text-gray-900">{tenant.enterpriseInfo.legalPersonName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">法人身份证号码</label>
                    <p className="text-gray-900">{tenant.enterpriseInfo.legalPersonIdCard}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">营业场所/住所</label>
                    <p className="text-gray-900">{tenant.enterpriseInfo.businessAddress}</p>
                  </div>
                </div>
              </div>
            )}

            {tenant.type === 'school' && tenant.schoolInfo && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">学校信息</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">学校名称</label>
                    <p className="text-gray-900">{tenant.schoolInfo.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">学校类型</label>
                    <p className="text-gray-900">{getSchoolTypeLabel(tenant.schoolInfo.type)}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">学校地址</label>
                    <p className="text-gray-900">
                      {tenant.schoolInfo.province} {tenant.schoolInfo.city} {tenant.schoolInfo.district} {tenant.schoolInfo.detailAddress}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系人</label>
                <p className="text-gray-900">{tenant.contact}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系电话</label>
                <p className="text-gray-900">{tenant.contactPhone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">创建日期</label>
                <p className="text-gray-900">{tenant.createDate}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">管理员</label>
                <p className="text-gray-900">{tenant.adminUsername}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">更新日期</label>
                <p className="text-gray-900">{tenant.updateDate}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">创建人</label>
                <p className="text-gray-900">{tenant.creator}</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                <p className="text-gray-900">{tenant.remark || '-'}</p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">资产信息</h3>
                <button
                  onClick={() => setShowRecharge(true)}
                  className="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  充值
                </button>
              </div>
              
              {tenant.type === 'enterprise' && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">企业钱包</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">余额</label>
                      <p className="text-lg font-semibold text-gray-900">¥{tenant.wallet.balance.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">累积充值</label>
                      <p className="text-lg font-semibold text-gray-900">¥{tenant.wallet.totalRecharge.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">累积消费</label>
                      <p className="text-lg font-semibold text-gray-900">¥{tenant.wallet.totalConsumption.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {tenant.type === 'school' && tenant.points && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    积分
                    <div className="relative group">
                      <i className="ri-information-line text-gray-400 cursor-pointer"></i>
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                        已配置积分：指学校已经从平台获得的总积分额度，可用于分配给学生使用
                      </div>
                    </div>
                  </h4>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">可用积分</label>
                      <p className="text-lg font-semibold text-gray-900">{tenant.points.available.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">已配置积分：{tenant.points.configured.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">已消费积分</label>
                      <p className="text-lg font-semibold text-gray-900">{tenant.points.consumed.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">剩余未消费积分</label>
                      <p className="text-lg font-semibold text-gray-900">{tenant.points.remaining.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">累积充值积分</label>
                      <p className="text-gray-900">{tenant.points.totalRecharge.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">累积消费积分</label>
                      <p className="text-gray-900">{tenant.points.totalConsumption.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">累积过期积分</label>
                      <p className="text-gray-900">{tenant.points.totalExpired.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">机房网络情况</h3>
              <div className="space-y-6">
                {tenant.datacenterNetworks.map((network, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-semibold text-gray-900">{network.datacenter}</h4>
                      <span className={`px-3 py-1 text-xs rounded-full ${
                        network.status === 'normal' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {network.status === 'normal' ? '正常' : '异常'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      {/* 网络延迟 */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-time-line text-gray-500"></i>
                          <span className="text-sm text-gray-600">网络延迟</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-2xl font-semibold ${
                            network.latency < 10 ? 'text-green-600' : 
                            network.latency < 20 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {network.latency}
                          </span>
                          <span className="text-sm text-gray-500">ms</span>
                        </div>
                      </div>

                      {/* 丢包率 */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-error-warning-line text-gray-500"></i>
                          <span className="text-sm text-gray-600">丢包率</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-2xl font-semibold ${
                            network.packetLoss < 0.1 ? 'text-green-600' : 
                            network.packetLoss < 0.2 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {network.packetLoss}
                          </span>
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>

                      {/* 网络带宽 */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-speed-line text-gray-500"></i>
                          <span className="text-sm text-gray-600">网络带宽</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-semibold text-blue-600">
                            {network.bandwidth}
                          </span>
                          <span className="text-sm text-gray-500">Mbps</span>
                        </div>
                      </div>

                      {/* 在线率 */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-checkbox-circle-line text-gray-500"></i>
                          <span className="text-sm text-gray-600">在线率</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-2xl font-semibold ${
                            network.uptime >= 99.5 ? 'text-green-600' : 
                            network.uptime >= 98 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {network.uptime}
                          </span>
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">充值日志</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">账号</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">时间</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">金额</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tenant.rechargeLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{log.account}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{log.ip}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{log.time}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">
                          {tenant.type === 'enterprise' ? `¥${log.amount.toLocaleString()}` : `${log.amount.toLocaleString()} 积分`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">确认禁用</h3>
            <p className="text-gray-600 mb-6">确定要禁用租户"{tenant.name}"吗？</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleConfirmDisable}
                className="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecharge && (
        <RechargeModal
          tenant={tenant}
          onClose={() => setShowRecharge(false)}
          onSuccess={() => {
            setShowRecharge(false);
          }}
        />
      )}
    </>
  );
}
